'use client';

import { Canvas, useThree, useFrame } from '@react-three/fiber';
import {
  OrbitControls,
  Environment,
  TransformControls,
  Grid,
  GizmoHelper,
  GizmoViewport,
} from '@react-three/drei';
import {
  EffectComposer,
  DepthOfField,
  Bloom,
  Vignette,
  ChromaticAberration,
  FXAA,
  N8AO,
  ToneMapping,
  Outline,
} from '@react-three/postprocessing';
import { BlendFunction, ToneMappingMode, Effect } from 'postprocessing';
import { Suspense, type ReactNode, useRef, useEffect, useState, useMemo, forwardRef } from 'react';
import { ConfigureGltfLoaders } from './gltf-loader-setup';
import * as THREE from 'three';
import { EditorTaaRoot, EditorTemporalResolvePass, useEditorTaaRef } from './editor-taa';
import { IdleSupersampler } from './idle-supersampler';
import { ResolvedSceneEnvironment } from './resolved-scene-environment';
import { cameraLinkedEnvYaw, getOrbitTarget } from './camera-env-link';
import { applyMaterialTextureLodBias, configureStableTextureSampling, textureHasStableMinFilter, ensureTextureLodBiasShaderChunks } from './texture-sampling';
import { NestedTransmissionApplier } from './nested-transmission-applier';
import { patchTransmissionBlurShader, clampTransmissionResolutionScale, patchTransmissionRenderTargetNoMsaa } from './transmission-blur-patch';
import { patchCompileAsyncSafe, patchCompileAsyncOnRenderer } from './compile-async-safe';
import { nativeCanvasMsaa, postFxNeedsComposer, postFxNeedsHdrComposer, postFxUsesRendererToneMap, resolveComposerMsaaSamples, TunableSmaa } from './smaa-effect';

patchTransmissionBlurShader();
ensureTextureLodBiasShaderChunks();
patchTransmissionRenderTargetNoMsaa();
patchCompileAsyncSafe();

// ---------------------------------------------------------------------------
// Types matching the editor store
// ---------------------------------------------------------------------------
export type TransformMode = 'translate' | 'rotate' | 'scale';

export interface PostFXSettings {
  enabled: boolean;
  toneMapping: string;
  exposure: number;
  dof: { enabled: boolean; focusDistance: number; focalLength: number; bokehScale: number };
  bloom: { enabled: boolean; intensity: number; threshold: number; radius: number };
  vignette: { enabled: boolean; offset: number; darkness: number };
  chromaticAberration: { enabled: boolean; offset: number };
  ssao: { enabled: boolean; radius: number; intensity: number; samples?: number; denoiseSamples?: number; denoiseRadius?: number; distanceFalloff?: number; screenSpaceRadius?: boolean; lightnessOnly?: boolean; excludeActive?: boolean };
  filmGrain?: { enabled: boolean; intensity: number };
  sharpenBlur?: { enabled: boolean; sharpenAmount: number; sharpenRadius: number; blurRadius: number };
  antialiasing: 'none' | 'smaa' | 'msaa' | 'fxaa' | 'taa';
  canvasAntialias?: boolean;
  outputMsaaSamples?: number;
  smaaPreset?: string;
  dprMin?: number;
  dprMax?: number;
  textureMaxAnisotropy?: number;
  textureLodBias?: number;
}

export interface EditorCanvasProps {
  children?: ReactNode;
  environmentPreset?: string;
  hdrUrl?: string;
  envIntensity?: number;
  envBgBlur?: number;
  envShowBg?: boolean;
  envLinkToCamera?: boolean;
  envRotationY?: number;
  envFlipH?: boolean;
  envFlipV?: boolean;
  bgMode?: string;
  bgSolidColor?: string;
  bgGradient?: { color1: string; color2: string; type: 'linear' | 'radial'; angle: number };
  bgImageUrl?: string;
  bgImageFit?: 'cover' | 'contain' | 'fill';
  bgImageAdjust?: { brightness?: number; contrast?: number; saturation?: number; temperature?: number };
  shadows?: boolean;
  shadowType?: 'basic' | 'pcf' | 'pcfsoft' | 'vsm';
  showGrid?: boolean;
  showAxes?: boolean;

  selectedObjectRef?: React.RefObject<THREE.Object3D | null>;
  transformMode?: TransformMode;
  transformSpace?: 'world' | 'local';
  snapEnabled?: boolean;
  snapTranslate?: number;
  snapRotate?: number;
  snapScale?: number;

  cameraLocked?: boolean;
  disableNavigation?: boolean;
  orthoView?: 'top' | 'bottom' | 'front' | 'back' | 'left' | 'right' | null;

  onTransformChange?: (position: THREE.Vector3, rotation: THREE.Euler, scale: THREE.Vector3) => void;
  onPointerMissed?: () => void;

  postfx?: PostFXSettings;
  /** When postfx.antialiasing is `taa`, changing this clears temporal history (reduces ghosting after visibility jumps). */
  postfxTaaResetKey?: string | number;

  safeFrameAspect?: number;

  viewportPosition?: [number, number, number];
  viewportTarget?: [number, number, number];
  viewportFov?: number;
  viewportNear?: number;
  viewportFar?: number;
  viewportLensShiftX?: number;
  viewportLensShiftY?: number;
  onViewportApplied?: () => void;
}

// ---------------------------------------------------------------------------
// Tone mapping mode lookup
// ---------------------------------------------------------------------------
const MATERIAL_MAP_TEXTURE_KEYS = [
  'map', 'lightMap', 'aoMap', 'emissiveMap', 'bumpMap', 'normalMap',
  'displacementMap', 'roughnessMap', 'metalnessMap', 'alphaMap',
  'clearcoatMap', 'clearcoatNormalMap', 'clearcoatRoughnessMap',
  'sheenColorMap', 'sheenRoughnessMap', 'specularColorMap', 'specularIntensityMap',
  'transmissionMap', 'thicknessMap', 'anisotropyMap',
  'iridescenceMap', 'iridescenceThicknessMap',
] as const;

function pickMsaaSamples(requested: number | undefined): number {
  const allowed = [0, 2, 4, 8, 16];
  const v = Math.max(0, Math.min(16, Math.round(requested ?? 0)));
  return allowed.reduce((best, cur) => (Math.abs(cur - v) < Math.abs(best - v) ? cur : best));
}

/** Cap MSAA so HalfFloat-sized buffers don't silently fail allocation at high DPR. */
function capMsaaForBuffer(samples: number, gl: THREE.WebGLRenderer): number {
  if (samples <= 0) return 0;
  const size = new THREE.Vector2();
  gl.getDrawingBufferSize(size);
  const px = Math.max(1, size.x * size.y);
  if (px > 16_000_000) return Math.min(samples, 4);
  return Math.min(samples, 8);
}

function TextureAnisotropyCap({
  maxAnisotropy,
  lodBias = 1,
}: {
  maxAnisotropy: number;
  lodBias?: number;
}) {
  const { gl, scene } = useThree();
  const cap = Math.max(1, Math.min(maxAnisotropy, gl.capabilities.getMaxAnisotropy()));
  useFrame(() => {
    scene.traverse((obj) => {
      if (!(obj instanceof THREE.Mesh)) return;
      const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
      for (const mat of mats) {
        if (!mat || typeof mat !== 'object') continue;
        applyMaterialTextureLodBias(mat as THREE.Material, lodBias);
        for (const key of MATERIAL_MAP_TEXTURE_KEYS) {
          const t = (mat as Record<string, unknown>)[key];
          if (t && (t as THREE.Texture).isTexture) {
            const tex = t as THREE.Texture;
            // Mips first — anisotropy is ignored for non-mip minFilters in modern Three.
            if (!textureHasStableMinFilter(tex)) configureStableTextureSampling(tex);
            if (tex.anisotropy !== cap) tex.anisotropy = cap;
          }
        }
      }
    });
  });
  return null;
}

function getThreeToneMapping(name: string): THREE.ToneMapping {
  switch (name) {
    case 'reinhard':
      return THREE.ReinhardToneMapping;
    case 'agx':
      return THREE.AgXToneMapping;
    case 'neutral':
      return THREE.NeutralToneMapping;
    case 'aces':
    case 'filmic':
      return THREE.ACESFilmicToneMapping;
    default:
      return THREE.LinearToneMapping;
  }
}

function getToneMappingMode(name: string): ToneMappingMode {
  switch (name) {
    case 'reinhard': return ToneMappingMode.REINHARD;
    case 'reinhard2': return ToneMappingMode.REINHARD2;
    case 'aces': return ToneMappingMode.ACES_FILMIC;
    case 'filmic': return ToneMappingMode.ACES_FILMIC;
    case 'agx': return ToneMappingMode.AGX;
    case 'neutral': return ToneMappingMode.NEUTRAL;
    default: return ToneMappingMode.LINEAR;
  }
}

// ---------------------------------------------------------------------------
// N8AO HSL lightness-only shader patch
// ---------------------------------------------------------------------------
const N8AO_HSL_FUNCTIONS = /* glsl */`
vec3 _n8aoRgb2hsl(vec3 c) {
  float mx = max(max(c.r, c.g), c.b);
  float mn = min(min(c.r, c.g), c.b);
  float l = (mx + mn) * 0.5;
  if (mx == mn) return vec3(0.0, 0.0, l);
  float d = mx - mn;
  float s = l > 0.5 ? d / (2.0 - mx - mn) : d / (mx + mn);
  float h;
  if (mx == c.r) h = (c.g - c.b) / d + (c.g < c.b ? 6.0 : 0.0);
  else if (mx == c.g) h = (c.b - c.r) / d + 2.0;
  else h = (c.r - c.g) / d + 4.0;
  return vec3(h / 6.0, s, l);
}
float _n8aoHue2rgb(float p, float q, float t) {
  if (t < 0.0) t += 1.0;
  if (t > 1.0) t -= 1.0;
  if (t < 1.0/6.0) return p + (q - p) * 6.0 * t;
  if (t < 0.5) return q;
  if (t < 2.0/3.0) return p + (q - p) * (2.0/3.0 - t) * 6.0;
  return p;
}
vec3 _n8aoHsl2rgb(vec3 hsl) {
  if (hsl.y <= 0.0) return vec3(hsl.z);
  float q = hsl.z < 0.5 ? hsl.z * (1.0 + hsl.y) : hsl.z + hsl.y - hsl.z * hsl.y;
  float p = 2.0 * hsl.z - q;
  return vec3(
    _n8aoHue2rgb(p, q, hsl.x + 1.0/3.0),
    _n8aoHue2rgb(p, q, hsl.x),
    _n8aoHue2rgb(p, q, hsl.x - 1.0/3.0)
  );
}
vec3 _n8aoLightnessAO(vec3 rgb, float ao) {
  vec3 hsl = _n8aoRgb2hsl(rgb);
  hsl.z *= ao;
  return _n8aoHsl2rgb(hsl);
}
`;

// ---------------------------------------------------------------------------
// Sharpen + blur effect (shared fragment shader with viewer-canvas)
// ---------------------------------------------------------------------------
const SHARPEN_BLUR_FRAG = `
uniform float u_sharpenAmount;
uniform float u_sharpenRadius;
uniform float u_blurRadius;
void mainImage(const in vec4 inputColor, const in vec2 uv, out vec4 outputColor) {
  vec2 texelSize = 1.0 / resolution;
  float bR = u_blurRadius;
  vec4 blurred = inputColor * 0.2270270270;
  blurred += texture2D(inputBuffer, uv + vec2( bR, 0.0) * texelSize) * 0.1945945946;
  blurred += texture2D(inputBuffer, uv + vec2(-bR, 0.0) * texelSize) * 0.1945945946;
  blurred += texture2D(inputBuffer, uv + vec2(0.0,  bR) * texelSize) * 0.1945945946;
  blurred += texture2D(inputBuffer, uv + vec2(0.0, -bR) * texelSize) * 0.1945945946;
  if (u_sharpenAmount <= 0.0) { outputColor = (bR > 0.0) ? blurred : inputColor; return; }
  float sR = u_sharpenRadius;
  vec4 sBlur = inputColor * 0.2270270270;
  sBlur += texture2D(inputBuffer, uv + vec2( sR, 0.0) * texelSize) * 0.1945945946;
  sBlur += texture2D(inputBuffer, uv + vec2(-sR, 0.0) * texelSize) * 0.1945945946;
  sBlur += texture2D(inputBuffer, uv + vec2(0.0,  sR) * texelSize) * 0.1945945946;
  sBlur += texture2D(inputBuffer, uv + vec2(0.0, -sR) * texelSize) * 0.1945945946;
  vec4 sharpened = inputColor + u_sharpenAmount * (inputColor - sBlur);
  if (bR > 0.0) {
    vec4 finalBlur = sharpened * 0.2270270270;
    finalBlur += texture2D(inputBuffer, uv + vec2( bR, 0.0) * texelSize) * 0.1945945946;
    finalBlur += texture2D(inputBuffer, uv + vec2(-bR, 0.0) * texelSize) * 0.1945945946;
    finalBlur += texture2D(inputBuffer, uv + vec2(0.0,  bR) * texelSize) * 0.1945945946;
    finalBlur += texture2D(inputBuffer, uv + vec2(0.0, -bR) * texelSize) * 0.1945945946;
    outputColor = finalBlur;
  } else { outputColor = sharpened; }
}
`;

class EditorSharpenBlurEffect extends Effect {
  constructor({ sharpenAmount = 1.0, sharpenRadius = 0.5, blurRadius = 0 } = {}) {
    super('SharpenBlurEffect', SHARPEN_BLUR_FRAG, {
      blendFunction: BlendFunction.NORMAL,
      uniforms: new Map<string, THREE.Uniform>([
        ['u_sharpenAmount', new THREE.Uniform(sharpenAmount)],
        ['u_sharpenRadius', new THREE.Uniform(sharpenRadius)],
        ['u_blurRadius', new THREE.Uniform(blurRadius)],
      ]),
    });
  }
}

const EditorSharpenBlurPass = forwardRef(function EditorSharpenBlurPassInner(
  { sharpenAmount, sharpenRadius, blurRadius }: { sharpenAmount: number; sharpenRadius: number; blurRadius: number },
  ref: React.Ref<EditorSharpenBlurEffect>,
) {
  const effect = useMemo(() => new EditorSharpenBlurEffect({ sharpenAmount, sharpenRadius, blurRadius }), []);
  useEffect(() => {
    effect.uniforms.get('u_sharpenAmount')!.value = sharpenAmount;
    effect.uniforms.get('u_sharpenRadius')!.value = sharpenRadius;
    effect.uniforms.get('u_blurRadius')!.value = blurRadius;
  }, [effect, sharpenAmount, sharpenRadius, blurRadius]);
  return <primitive ref={ref} object={effect} dispose={null} />;
});

// ---------------------------------------------------------------------------
// Post-processing layer
// ---------------------------------------------------------------------------

/**
 * EffectComposer reads `gl.getContextAttributes().alpha` on mount. After
 * "Too many active WebGL contexts" the attributes object is null and the
 * whole editor crashes. Skip postFX until the context is healthy again.
 */
function SafeEffectComposer({
  multisampling,
  frameBufferType,
  children,
}: {
  multisampling: number;
  frameBufferType?: THREE.TextureDataType;
  children: React.ReactElement | React.ReactElement[];
}) {
  const gl = useThree((s) => s.gl);
  const [alive, setAlive] = useState(() => !!gl.getContextAttributes?.());

  useEffect(() => {
    const canvas = gl.domElement;
    const onLost = (e: Event) => {
      e.preventDefault();
      setAlive(false);
    };
    const onRestored = () => setAlive(!!gl.getContextAttributes?.());
    canvas.addEventListener('webglcontextlost', onLost, false);
    canvas.addEventListener('webglcontextrestored', onRestored, false);
    setAlive(!!gl.getContextAttributes?.());
    return () => {
      canvas.removeEventListener('webglcontextlost', onLost, false);
      canvas.removeEventListener('webglcontextrestored', onRestored, false);
    };
  }, [gl]);

  if (!alive || !gl.getContextAttributes?.()) return null;

  return (
    <EffectComposer
      multisampling={multisampling}
      depthBuffer
      frameBufferType={frameBufferType ?? (multisampling > 0 ? THREE.UnsignedByteType : THREE.HalfFloatType)}
    >
      {children}
    </EffectComposer>
  );
}

function PostProcessingPipeline({
  settings,
  selectedRef,
  outlineRef,
  taaResolve = false,
}: {
  settings?: PostFXSettings;
  selectedRef?: React.RefObject<THREE.Object3D | null>;
  outlineRef: React.RefObject<any>;
  taaResolve?: boolean;
}) {
  const taaRef = useEditorTaaRef();
  const size = useThree((s) => s.size);
  const gl = useThree((s) => s.gl);
  const n8aoRef = useRef<any>(null);
  const ssaoLightnessOnly = settings?.ssao?.lightnessOnly ?? false;
  const ssaoExcludeActive = settings?.ssao?.excludeActive ?? false;

  useFrame(() => {
    if (!gl.getContextAttributes?.()) return;
    const pass = n8aoRef.current;
    if (!pass?.effectCompositerQuad?.material) return;

    if (ssaoExcludeActive && pass.configuration && !pass.configuration.transparencyAware) {
      pass.configuration.transparencyAware = true;
    }

    const mat = pass.effectCompositerQuad.material;
    const patched = mat.fragmentShader.includes('_n8aoRgb2hsl');
    if (ssaoLightnessOnly && !patched) {
      let fs = mat.fragmentShader;
      fs = fs.replace('void main() {', N8AO_HSL_FUNCTIONS + '\nvoid main() {');
      fs = fs.replace(/mix\(sceneTexel\.rgb,\s*aoApplied,\s*1\.0\s*-\s*finalAo\)/g, '_n8aoLightnessAO(sceneTexel.rgb, finalAo)');
      fs = fs.replace(/mix\(vec3\(1\.0\),\s*aoApplied,\s*1\.0\s*-\s*finalAo\)/g, 'vec3(finalAo)');
      mat.fragmentShader = fs;
      mat.needsUpdate = true;
    } else if (!ssaoLightnessOnly && patched) {
      pass.configureEffectCompositer?.(
        pass.configuration?.depthBufferType,
        pass.camera?.isOrthographicCamera,
      );
    }
  });

  if (size.width < 4 || size.height < 4) return null;

  const effects: React.ReactElement[] = [];

  // Selection outline – softer blend avoids a bright “halo” on silhouettes vs flat bg
  // (high strength + xRay + no blur reads as a harsh fringe in the viewport).
  if (selectedRef) {
    effects.push(
      <Outline
        key="selection-outline"
        ref={outlineRef}
        visibleEdgeColor={0x3388ff}
        hiddenEdgeColor={0x1a4480}
        edgeStrength={1.35}
        pulseSpeed={0}
        blur
        xRay={false}
      />,
    );
  }

  if (settings?.enabled && postFxNeedsHdrComposer(settings)) {
    effects.push(
      <ToneMapping key="tm" mode={getToneMappingMode(settings.toneMapping)} />,
    );

    if (settings.dof.enabled) {
      effects.push(
        <DepthOfField
          key="dof"
          focusDistance={settings.dof.focusDistance}
          focalLength={settings.dof.focalLength}
          bokehScale={settings.dof.bokehScale}
        />,
      );
    }
    if (settings.bloom.enabled) {
      effects.push(
        <Bloom
          key="bloom"
          intensity={settings.bloom.intensity}
          luminanceThreshold={settings.bloom.threshold}
          luminanceSmoothing={settings.bloom.radius}
        />,
      );
    }
    if (settings.vignette.enabled) {
      effects.push(
        <Vignette
          key="vig"
          offset={settings.vignette.offset}
          darkness={settings.vignette.darkness}
          blendFunction={BlendFunction.NORMAL}
        />,
      );
    }
    if (settings.chromaticAberration.enabled) {
      effects.push(
        <ChromaticAberration
          key="ca"
          offset={new THREE.Vector2(settings.chromaticAberration.offset, settings.chromaticAberration.offset)}
          blendFunction={BlendFunction.NORMAL}
        />,
      );
    }
    if (settings.ssao.enabled) {
      const s = settings.ssao;
      effects.push(
        <N8AO
          ref={n8aoRef}
          key={`n8ao-${s.radius}-${s.intensity}-${s.samples ?? 16}-${s.denoiseSamples ?? 8}-${s.denoiseRadius ?? 12}-${s.distanceFalloff ?? 1}`}
          aoRadius={s.radius}
          intensity={s.intensity}
          aoSamples={s.samples ?? 16}
          denoiseSamples={s.denoiseSamples ?? 8}
          denoiseRadius={s.denoiseRadius ?? 12}
          distanceFalloff={s.distanceFalloff ?? 1}
          screenSpaceRadius={s.screenSpaceRadius ?? false}
          depthAwareUpsampling
        />,
      );
    }
    if (settings.sharpenBlur?.enabled) {
      effects.push(
        <EditorSharpenBlurPass
          key="sharpenBlur"
          sharpenAmount={Math.min(settings.sharpenBlur.sharpenAmount, 1.0)}
          sharpenRadius={settings.sharpenBlur.sharpenRadius}
          blurRadius={settings.sharpenBlur.blurRadius}
        />,
      );
    }
  }

  // SMAA / MSAA: apply even when the Post FX master toggle is off so zoomed-out /
  // distant views still get edge anti-aliasing (otherwise only 4× MSAA from the canvas).
  const aa = settings?.antialiasing;
  if (aa === 'fxaa') {
    effects.push(<FXAA key="fxaa" />);
  }
  if (aa === 'smaa') {
    effects.push(<TunableSmaa key="smaa" preset={settings?.smaaPreset ?? 'ultra'} />);
  }

  // Progressive-SSAA accumulator — after MSAA+SMAA so it averages the fully resolved
  // frame. Pass-through until the camera is idle and jitter runs.
  // For `taa` mode this same pass IS the anti-aliasing.
  if (taaResolve && taaRef) {
    effects.push(<EditorTemporalResolvePass key="taa" stateRef={taaRef} />);
  }

  // EffectComposer replaces the canvas backbuffer, so WebGL `antialias` never
  // reaches the scene. SMAA without composer MSAA is a 1-sample filter → jagged.
  // HalfFloat + MSAA fails allocation → byte buffers. Cap 4×: 8× + transmission
  // regularly loses the GL context on Windows/Chromium.
  const requestedMsaa = resolveComposerMsaaSamples(aa, settings?.outputMsaaSamples);
  const cappedMsaa = capMsaaForBuffer(pickMsaaSamples(requestedMsaa), gl);
  const maxSamples = typeof gl.capabilities?.maxSamples === 'number' ? gl.capabilities.maxSamples : cappedMsaa;
  const multisampling = requestedMsaa > 0
    ? Math.max(0, Math.min(cappedMsaa, maxSamples || cappedMsaa, 8))
    : 0;
  if (effects.length === 0 && multisampling === 0) return null;

  return (
    <SafeEffectComposer
      key={`pp-${aa ?? 'none'}-${settings?.smaaPreset ?? 'ultra'}-${multisampling}`}
      multisampling={multisampling}
      frameBufferType={multisampling > 0 ? THREE.UnsignedByteType : THREE.HalfFloatType}
    >
      {effects}
    </SafeEffectComposer>
  );
}

function PostFXLayer({
  settings,
  selectedRef,
  outlineRef,
  taaResolve = false,
}: {
  settings?: PostFXSettings;
  selectedRef?: React.RefObject<THREE.Object3D | null>;
  outlineRef: React.RefObject<any>;
  taaResolve?: boolean;
}) {
  return (
    <PostProcessingPipeline
      settings={settings}
      selectedRef={selectedRef}
      outlineRef={outlineRef}
      taaResolve={taaResolve}
    />
  );
}

// ---------------------------------------------------------------------------
// Sync environment rotation to camera azimuth so reflections stay fixed
// relative to the viewport, giving the impression the object rotates.
// ---------------------------------------------------------------------------
function EnvironmentRotationSync({
  enabled,
  rotationY = 0,
  flipH = false,
  flipV = false,
}: {
  enabled: boolean;
  rotationY?: number;
  flipH?: boolean;
  flipV?: boolean;
}) {
  const { scene, camera, controls } = useThree();

  useFrame(() => {
    const baseY = rotationY + (flipH ? Math.PI : 0);
    const baseX = flipV ? Math.PI : 0;

    let targetY = baseY;
    if (enabled) {
      targetY += cameraLinkedEnvYaw(camera, getOrbitTarget(controls));
    }

    if (!scene.environmentRotation) {
      (scene as any).environmentRotation = new THREE.Euler(baseX, targetY, 0);
    } else {
      scene.environmentRotation.x = baseX;
      scene.environmentRotation.y = targetY;
    }

    if (scene.backgroundRotation) {
      scene.backgroundRotation.x = baseX;
      scene.backgroundRotation.y = targetY;
    } else {
      (scene as any).backgroundRotation = new THREE.Euler(baseX, targetY, 0);
    }
  });

  return null;
}

// ---------------------------------------------------------------------------
// Gizmo that tracks transform changes
// ---------------------------------------------------------------------------
function isInSceneGraph(obj: THREE.Object3D): boolean {
  let current: THREE.Object3D | null = obj;
  while (current) {
    if (current instanceof THREE.Scene) return true;
    current = current.parent;
  }
  return false;
}

function TransformGizmo({
  selectedRef,
  mode,
  space,
  snapEnabled,
  snapTranslate,
  snapRotate,
  snapScale,
  onTransformChange,
}: {
  selectedRef: React.RefObject<THREE.Object3D | null>;
  mode: TransformMode;
  space: 'world' | 'local';
  snapEnabled: boolean;
  snapTranslate: number;
  snapRotate: number;
  snapScale: number;
  onTransformChange?: EditorCanvasProps['onTransformChange'];
}) {
  const controlsRef = useRef<any>(null);

  useEffect(() => {
    const controls = controlsRef.current;
    if (!controls) return;

    const handleChange = () => {
      const obj = selectedRef.current;
      if (!obj || !onTransformChange) return;
      onTransformChange(obj.position.clone(), obj.rotation.clone(), obj.scale.clone());
    };

    controls.addEventListener('objectChange', handleChange);
    return () => controls.removeEventListener('objectChange', handleChange);
  }, [selectedRef, onTransformChange]);

  useFrame(() => {
    const controls = controlsRef.current;
    if (!controls) return;
    const obj = controls.object;
    if (obj && !isInSceneGraph(obj)) {
      controls.detach();
    }
  });

  const obj = selectedRef.current;
  if (!obj || !isInSceneGraph(obj)) return null;

  const degToRad = (d: number) => (d * Math.PI) / 180;

  return (
    <TransformControls
      ref={controlsRef}
      object={obj}
      mode={mode}
      space={space}
      translationSnap={snapEnabled ? snapTranslate : null}
      rotationSnap={snapEnabled ? degToRad(snapRotate) : null}
      scaleSnap={snapEnabled ? snapScale : null}
    />
  );
}

// ---------------------------------------------------------------------------
// Selection outline – syncs the selected object's meshes to the Outline
// effect imperatively via ref, avoiding React re-renders on each frame.
// ---------------------------------------------------------------------------
function SelectionOutlineSync({
  selectedRef,
  outlineRef,
}: {
  selectedRef: React.RefObject<THREE.Object3D | null>;
  outlineRef: React.RefObject<any>;
}) {
  const prevSigRef = useRef('');

  useFrame(() => {
    const effect = outlineRef.current;
    if (!effect?.selection) return;

    const obj = selectedRef.current;
    if (!obj || !isInSceneGraph(obj)) {
      if (prevSigRef.current !== '') {
        effect.selection.clear();
        prevSigRef.current = '';
      }
      // SCREEN-blended outline can still tint the frame if the pass runs with stale masks; zero opacity when idle.
      effect.blendMode?.setOpacity?.(0);
      return;
    }

    effect.blendMode?.setOpacity?.(1);

    const targets: THREE.Object3D[] = [];
    obj.traverse((child: THREE.Object3D) => {
      if ((child as THREE.Mesh).isMesh && child.visible) {
        targets.push(child);
      }
      if ((child as THREE.InstancedMesh).isInstancedMesh) {
        targets.push(child);
      }
    });

    let p: THREE.Object3D | null = obj;
    while (p) {
      const getter = (p.userData as { getC3dInstancedMeshes?: () => THREE.InstancedMesh[] }).getC3dInstancedMeshes;
      if (typeof getter === 'function') {
        for (const im of getter()) {
          if (im) targets.push(im);
        }
      }
      p = p.parent;
    }

    const sig = targets.map((t) => `${t.uuid}:${(t as THREE.InstancedMesh).isInstancedMesh ? (t as THREE.InstancedMesh).count : ''}`).join('|');
    if (sig === prevSigRef.current) return;
    prevSigRef.current = sig;

    effect.selection.clear();
    for (const t of targets) {
      effect.selection.add(t);
    }
  });

  return null;
}

// ---------------------------------------------------------------------------
// Transmission quality – see transmission-blur-patch.ts
// ---------------------------------------------------------------------------
function TransmissionQuality({ scale = 2 }: { scale?: number }) {
  const { gl, size } = useThree();
  useEffect(() => {
    const dpr = gl.getPixelRatio?.() || 1;
    const safe = clampTransmissionResolutionScale(
      scale,
      gl,
      size.width * dpr,
      size.height * dpr,
    );
    (gl as any).transmissionResolutionScale = safe;
    return () => { (gl as any).transmissionResolutionScale = 1; };
  }, [gl, scale, size.width, size.height]);
  return null;
}

// ---------------------------------------------------------------------------
// Stronger geometric specular anti-aliasing (variance-based)
// ---------------------------------------------------------------------------
(() => {
  const chunk = THREE.ShaderChunk as Record<string, string>;
  const key = 'lights_physical_fragment';
  if (!chunk[key]) return;
  const weak = 'vec3 dxy = max( abs( dFdx( nonPerturbedNormal ) ), abs( dFdy( nonPerturbedNormal ) ) );\nfloat geometryRoughness = max( max( dxy.x, dxy.y ), dxy.z );';
  // Milder than the old *20 / 0.35 cap — that washed anisotropy into round blobs on
  // high-curvature Array Radial ribs while the smooth material-shelf sphere still looked brushed.
  const strong = [
    'vec3 dNdx = dFdx( nonPerturbedNormal );',
    'vec3 dNdy = dFdy( nonPerturbedNormal );',
    'float geometryRoughness = min( ( dot( dNdx, dNdx ) + dot( dNdy, dNdy ) ) * 4.0, 0.12 );',
  ].join('\n');
  if (chunk[key].includes(weak)) {
    chunk[key] = chunk[key].replace(weak, strong);
  }
})();

// ---------------------------------------------------------------------------
// Exposure controller (updates renderer tone mapping)
// ---------------------------------------------------------------------------
function ExposureController({
  exposure,
  toneMapping,
  applyRendererToneMap,
}: {
  exposure: number;
  toneMapping?: string;
  applyRendererToneMap?: boolean;
}) {
  const { gl } = useThree();
  useEffect(() => {
    gl.toneMappingExposure = exposure;
    if (applyRendererToneMap) {
      gl.toneMapping = getThreeToneMapping(toneMapping ?? 'aces');
    }
  }, [gl, exposure, toneMapping, applyRendererToneMap]);
  return null;
}

// ---------------------------------------------------------------------------
// Viewport sync (snaps camera + orbit target on paste)
// ---------------------------------------------------------------------------
function hfovToVfov(hDeg: number, aspect: number): number {
  const hRad = (hDeg * Math.PI) / 180;
  return (2 * Math.atan(Math.tan(hRad / 2) / aspect) * 180) / Math.PI;
}

function FovSpeedSync() {
  const camera = useThree((s) => s.camera);
  const controls = useThree((s) => s.controls) as any;

  useFrame(() => {
    if (!controls || !(camera instanceof THREE.PerspectiveCamera)) return;
    const ratio = camera.fov / 50;
    const speed = Math.max(0.15, ratio * 1.5);
    if (Math.abs((controls.rotateSpeed ?? 1) - speed) > 0.01) {
      controls.rotateSpeed = speed;
      controls.zoomSpeed = Math.max(0.3, ratio * 1.2);
    }
  });

  return null;
}

function ViewportSync({
  position,
  target,
  fov,
  near,
  far,
  lensShiftX,
  lensShiftY,
  safeFrameAspect,
  onApplied,
}: {
  position?: [number, number, number];
  target?: [number, number, number];
  fov?: number;
  near?: number;
  far?: number;
  lensShiftX?: number;
  lensShiftY?: number;
  safeFrameAspect?: number;
  onApplied?: () => void;
}) {
  const camera = useThree((s) => s.camera);
  const controls = useThree((s) => s.controls) as any;
  const invalidate = useThree((s) => s.invalidate);
  const onAppliedRef = useRef(onApplied);
  onAppliedRef.current = onApplied;

  const sx = lensShiftX ?? 0;
  const sy = lensShiftY ?? 0;
  const shiftRef = useRef({ x: 0, y: 0 });
  const hfovRef = useRef(0);
  const sfAspectRef = useRef(0);

  useEffect(() => {
    sfAspectRef.current = safeFrameAspect && safeFrameAspect > 0 ? safeFrameAspect : 0;
  }, [safeFrameAspect]);

  useEffect(() => {
    if (!position || !target) return;
    if (!position.every(Number.isFinite) || !target.every(Number.isFinite)) return;
    const ctl = controls;
    if (!ctl || !('target' in ctl)) return;

    camera.position.set(position[0], position[1], position[2]);
    ctl.target.set(target[0], target[1], target[2]);
    ctl.update();

    if (camera instanceof THREE.PerspectiveCamera) {
      if (typeof fov === 'number' && fov > 0) {
        hfovRef.current = fov;
        const aspect = sfAspectRef.current > 0 ? sfAspectRef.current : camera.aspect;
        camera.fov = hfovToVfov(fov, aspect);
      }
      if (typeof near === 'number' && near > 0) camera.near = near;
      if (typeof far === 'number' && far > 0) camera.far = far;
    }

    camera.updateProjectionMatrix();

    shiftRef.current = { x: sx, y: sy };

    if (sx !== 0 || sy !== 0) {
      const m = camera.projectionMatrix.elements;
      m[8] += sx * 2;
      m[9] -= sy * 2;
    }

    invalidate();
    onAppliedRef.current?.();
  }, [position?.[0], position?.[1], position?.[2], target?.[0], target?.[1], target?.[2], fov, near, far, sx, sy, camera, invalidate, controls]);

  useFrame(() => {
    if (!(camera instanceof THREE.PerspectiveCamera)) return;
    const pc = camera;

    const hfov = hfovRef.current;
    const { x: shiftX, y: shiftY } = shiftRef.current;
    const hasShift = shiftX !== 0 || shiftY !== 0;
    let needsUpdate = false;

    if (hfov > 0) {
      const aspect = sfAspectRef.current > 0 ? sfAspectRef.current : pc.aspect;
      const vfov = hfovToVfov(hfov, aspect);
      if (Math.abs(pc.fov - vfov) > 0.01) {
        pc.fov = vfov;
        needsUpdate = true;
      }
    }

    if (hasShift) {
      const m = pc.projectionMatrix.elements;
      if (Math.abs(m[8] - shiftX * 2) > 0.0001 || Math.abs(m[9] - (-shiftY * 2)) > 0.0001) {
        needsUpdate = true;
      }
    }

    if (needsUpdate) {
      pc.updateProjectionMatrix();
      if (hasShift) {
        const m = pc.projectionMatrix.elements;
        m[8] += shiftX * 2;
        m[9] -= shiftY * 2;
      }
    }
  });

  return null;
}

// ---------------------------------------------------------------------------
// SceneBgController – clears the GL buffer to transparent when CSS bg is used
// ---------------------------------------------------------------------------
/**
 * Clears / fills the GL buffer. Solid colors must live *inside* the scene
 * (not as a CSS layer behind a transparent canvas) — otherwise MSAA / composer
 * resolve against clear-black alpha and fringe over the CSS bg, which reads as
 * an unsharp-mask / sharpening halo on every silhouette.
 */
function SceneBgController({
  transparent,
  solidColor,
}: {
  transparent: boolean;
  solidColor?: string;
}) {
  const { gl, scene } = useThree();

  useEffect(() => {
    if (transparent) {
      scene.background = null;
      gl.setClearColor(0x000000, 0);
      return;
    }
    if (solidColor) {
      const c = new THREE.Color(solidColor);
      scene.background = c;
      gl.setClearColor(c, 1);
      return;
    }
    gl.setClearColor(0x000000, 1);
  }, [transparent, solidColor, gl, scene]);

  return null;
}

/** Bake a CSS-like gradient into scene.background so AA samples the real colors. */
function SceneGradientBackground({
  gradient,
}: {
  gradient: { type: string; color1: string; color2: string; angle: number };
}) {
  const { scene, size } = useThree();
  const texRef = useRef<THREE.CanvasTexture | null>(null);

  useEffect(() => {
    if (size.width < 1 || size.height < 1) return;
    const c = document.createElement('canvas');
    const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
    c.width = Math.min(Math.max(1, Math.round(size.width * dpr)), 2048);
    c.height = Math.min(Math.max(1, Math.round(size.height * dpr)), 2048);
    const ctx = c.getContext('2d')!;
    if (gradient.type === 'radial') {
      const g = ctx.createRadialGradient(
        c.width / 2, c.height / 2, 0,
        c.width / 2, c.height / 2, Math.max(c.width, c.height) / 2,
      );
      g.addColorStop(0, gradient.color1);
      g.addColorStop(1, gradient.color2);
      ctx.fillStyle = g;
    } else {
      const rad = ((gradient.angle ?? 180) * Math.PI) / 180;
      const x0 = c.width / 2 - Math.cos(rad) * c.width;
      const y0 = c.height / 2 - Math.sin(rad) * c.height;
      const x1 = c.width / 2 + Math.cos(rad) * c.width;
      const y1 = c.height / 2 + Math.sin(rad) * c.height;
      const g = ctx.createLinearGradient(x0, y0, x1, y1);
      g.addColorStop(0, gradient.color1);
      g.addColorStop(1, gradient.color2);
      ctx.fillStyle = g;
    }
    ctx.fillRect(0, 0, c.width, c.height);
    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.needsUpdate = true;
    const prev = texRef.current;
    texRef.current = tex;
    scene.background = tex;
    prev?.dispose();
    return () => {
      if (scene.background === tex) scene.background = null;
      tex.dispose();
      if (texRef.current === tex) texRef.current = null;
    };
  }, [scene, size.width, size.height, gradient.type, gradient.color1, gradient.color2, gradient.angle]);

  return null;
}

// ---------------------------------------------------------------------------
// SceneBackgroundImage – renders bg image inside the 3D scene to avoid
// alpha-compositing artifacts (dark fringes on bright backgrounds with MSAA)
// ---------------------------------------------------------------------------
function SceneBackgroundImage({
  url,
  fit = 'cover',
  brightness = 1,
  contrast = 1,
  saturation = 1,
  temperature = 0,
}: {
  url: string;
  fit?: 'cover' | 'contain' | 'fill';
  brightness?: number;
  contrast?: number;
  saturation?: number;
  temperature?: number;
}) {
  const { scene, size } = useThree();
  const texRef = useRef<THREE.CanvasTexture | null>(null);
  const [img, setImg] = useState<HTMLImageElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    const el = new Image();
    el.crossOrigin = 'anonymous';
    el.onload = () => { if (!cancelled) setImg(el); };
    el.src = url;
    return () => { cancelled = true; setImg(null); };
  }, [url]);

  useEffect(() => {
    if (!img || size.width === 0 || size.height === 0) return;

    const c = document.createElement('canvas');
    c.width = Math.min(size.width * (typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1), 2048);
    c.height = Math.min(size.height * (typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1), 2048);
    const ctx = c.getContext('2d')!;
    ctx.fillStyle = '#1a1a1e';
    ctx.fillRect(0, 0, c.width, c.height);

    const fp: string[] = [];
    if (brightness !== 1) fp.push(`brightness(${brightness})`);
    if (contrast !== 1) fp.push(`contrast(${contrast})`);
    if (saturation !== 1) fp.push(`saturate(${saturation})`);
    if (temperature !== 0) {
      fp.push(`sepia(${Math.min(Math.abs(temperature), 100)}%)`);
      if (temperature < 0) fp.push(`hue-rotate(180deg)`);
    }
    ctx.filter = fp.length > 0 ? fp.join(' ') : 'none';

    const iA = img.naturalWidth / img.naturalHeight;
    const vA = c.width / c.height;
    let dx = 0, dy = 0, dw = c.width, dh = c.height;
    if (fit === 'cover') {
      if (iA > vA) { dw = c.height * iA; dx = (c.width - dw) / 2; }
      else { dh = c.width / iA; dy = (c.height - dh) / 2; }
    } else if (fit === 'contain') {
      if (iA > vA) { dh = c.width / iA; dy = (c.height - dh) / 2; }
      else { dw = c.height * iA; dx = (c.width - dw) / 2; }
    }
    ctx.drawImage(img, dx, dy, dw, dh);

    texRef.current?.dispose();
    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    texRef.current = tex;
    scene.background = tex;
  }, [img, fit, size.width, size.height, scene, brightness, contrast, saturation, temperature]);

  useEffect(() => {
    return () => {
      if (scene.background === texRef.current) scene.background = null;
      texRef.current?.dispose();
    };
  }, [scene]);

  return null;
}

// ---------------------------------------------------------------------------
// Ortho view positions: camera placed at these positions looking at origin
// ---------------------------------------------------------------------------
const ORTHO_VIEWS: Record<string, { position: [number, number, number]; up: [number, number, number] }> = {
  top:    { position: [0,  50,  0.001], up: [0, 0, -1] },
  bottom: { position: [0, -50, -0.001], up: [0, 0,  1] },
  front:  { position: [0, 0,  50],  up: [0, 1, 0] },
  back:   { position: [0, 0, -50],  up: [0, 1, 0] },
  right:  { position: [50, 0, 0],   up: [0, 1, 0] },
  left:   { position: [-50, 0, 0],  up: [0, 1, 0] },
};

function OrthoViewManager({ orthoView }: { orthoView: string | null }) {
  const { camera, set, size, controls } = useThree();
  const perspCamRef = useRef<THREE.PerspectiveCamera | null>(null);
  const orthoCamRef = useRef<THREE.OrthographicCamera | null>(null);
  const perspStateRef = useRef<{ position: THREE.Vector3; target: THREE.Vector3; up: THREE.Vector3 } | null>(null);

  useEffect(() => {
    if (orthoView) {
      const viewData = ORTHO_VIEWS[orthoView];
      if (!viewData) return;

      if (camera instanceof THREE.PerspectiveCamera && !perspCamRef.current) {
        perspCamRef.current = camera;
        const orbitControls = controls as any;
        perspStateRef.current = {
          position: camera.position.clone(),
          target: orbitControls?.target ? (orbitControls.target as THREE.Vector3).clone() : new THREE.Vector3(),
          up: camera.up.clone(),
        };
      }

      const frustumSize = 10;
      const aspect = size.width / size.height;
      const orthoCam = orthoCamRef.current ?? new THREE.OrthographicCamera(
        -frustumSize * aspect / 2, frustumSize * aspect / 2,
        frustumSize / 2, -frustumSize / 2,
        0.01, 2000,
      );
      orthoCamRef.current = orthoCam;

      orthoCam.left = -frustumSize * aspect / 2;
      orthoCam.right = frustumSize * aspect / 2;
      orthoCam.top = frustumSize / 2;
      orthoCam.bottom = -frustumSize / 2;
      orthoCam.position.set(...viewData.position);
      orthoCam.up.set(...viewData.up);
      orthoCam.lookAt(0, 0, 0);
      orthoCam.zoom = 1;
      orthoCam.updateProjectionMatrix();

      set({ camera: orthoCam });
    } else if (perspCamRef.current) {
      const pc = perspCamRef.current;

      if (perspStateRef.current) {
        pc.position.copy(perspStateRef.current.position);
        pc.up.copy(perspStateRef.current.up);
      }

      set({ camera: pc });

      queueMicrotask(() => {
        const orbitControls = controls as any;
        if (orbitControls && perspStateRef.current) {
          orbitControls.target.copy(perspStateRef.current.target);
          orbitControls.update();
        }
        perspCamRef.current = null;
        perspStateRef.current = null;
      });
    }
  }, [orthoView]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!orthoView || !orthoCamRef.current) return;
    const aspect = size.width / size.height;
    const frustumSize = 10;
    const cam = orthoCamRef.current;
    cam.left = -frustumSize * aspect / 2;
    cam.right = frustumSize * aspect / 2;
    cam.top = frustumSize / 2;
    cam.bottom = -frustumSize / 2;
    cam.updateProjectionMatrix();
  }, [size.width, size.height, orthoView]);

  return null;
}

/** Prefer `.1k.hdr` when present; load HDR/EXR via loadEnvMap (safe parse). */
function ResolvedHdrEnvironment({
  hdrUrl,
  background,
  blur,
  environmentIntensity,
}: {
  hdrUrl: string;
  background: boolean;
  blur: number;
  environmentIntensity: number;
  resolution: number;
}) {
  return (
    <ResolvedSceneEnvironment
      hdrUrl={hdrUrl}
      background={background}
      blur={blur}
      environmentIntensity={environmentIntensity}
      preferHdrProxy
    />
  );
}

// ---------------------------------------------------------------------------
// EditorCanvas
// ---------------------------------------------------------------------------
export function EditorCanvas({
  children,
  environmentPreset = 'studio',
  hdrUrl,
  envIntensity = 1,
  envBgBlur = 0,
  envShowBg = false,
  envLinkToCamera = false,
  envRotationY = 0,
  envFlipH = false,
  envFlipV = false,
  bgMode = 'environment',
  bgSolidColor = '#1a1a1e',
  bgGradient,
  bgImageUrl,
  bgImageFit = 'cover',
  bgImageAdjust,
  shadows = true,
  /** `pcfsoft` maps to PCF — Three.js deprecated PCFSoftShadowMap (r178+). */
  shadowType = 'pcf',
  showGrid = true,
  showAxes = true,
  selectedObjectRef,
  transformMode = 'translate',
  transformSpace = 'world',
  snapEnabled = false,
  snapTranslate = 0.5,
  snapRotate = 15,
  snapScale = 0.1,
  cameraLocked = false,
  disableNavigation = false,
  orthoView = null,
  onTransformChange,
  onPointerMissed,
  postfx,
  postfxTaaResetKey,
  safeFrameAspect,
  viewportPosition,
  viewportTarget,
  viewportFov,
  viewportNear,
  viewportFar,
  viewportLensShiftX,
  viewportLensShiftY,
  onViewportApplied,
}: EditorCanvasProps) {
  const outlineRef = useRef<any>(null);
  // Backdrop pointer-down position — used to distinguish a real empty-space click
  // (deselect) from an orbit/pan drag that happens to release over the backdrop.
  const backdropDownRef = useRef<{ x: number; y: number } | null>(null);
  const showHdriBg = bgMode === 'environment' && envShowBg;
  const useSceneBgImage = bgMode === 'image' && !!bgImageUrl;
  const useSceneSolidBg = bgMode === 'solid';
  const useSceneGradientBg = bgMode === 'gradient' && !!bgGradient;
  // Only checkerboard "transparent" needs CSS-behind-canvas. Solid/gradient must
  // be opaque in-scene or MSAA fringes look like a permanent sharpen filter.
  const needTransparentGL = bgMode === 'transparent';
  const solidBgColor = useSceneSolidBg ? (bgSolidColor || '#1a1a1e') : undefined;

  const bgFilter = (() => {
    if (bgMode !== 'image' || useSceneBgImage || !bgImageAdjust) return undefined;
    const { brightness = 1, contrast = 1, saturation = 1, temperature = 0 } = bgImageAdjust;
    const parts: string[] = [];
    if (brightness !== 1) parts.push(`brightness(${brightness})`);
    if (contrast !== 1) parts.push(`contrast(${contrast})`);
    if (saturation !== 1) parts.push(`saturate(${saturation})`);
    if (temperature !== 0) {
      const t = Math.abs(temperature);
      parts.push(`sepia(${Math.min(t, 100)}%)`);
      if (temperature < 0) parts.push(`hue-rotate(180deg)`);
    }
    return parts.length > 0 ? parts.join(' ') : undefined;
  })();

  const dprLoRaw = Math.min(postfx?.dprMin ?? 1.5, postfx?.dprMax ?? 2.5);
  const dprHiRaw = Math.max(postfx?.dprMin ?? 1.5, postfx?.dprMax ?? 2.5);
  const dprLo = postfx?.antialiasing === 'taa' ? Math.min(dprLoRaw, 2) : dprLoRaw;
  const dprHi = postfx?.antialiasing === 'taa' ? Math.min(dprHiRaw, 2) : dprHiRaw;

  // Idle SSAA: single interact ratio on the Canvas, driven up when the camera is still.
  const idleSsaaEnabled = postfx?.antialiasing !== 'taa';
  const devicePixelRatioSafe = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
  const interactDpr = Math.max(2, devicePixelRatioSafe, dprLoRaw);
  const desiredIdleDpr = Math.max(interactDpr, dprHiRaw, 2);
  const composerOn = postFxNeedsComposer(postfx);
  const nativeMsaa = nativeCanvasMsaa(postfx);
  const [renderDpr, setRenderDpr] = useState(interactDpr);
  useEffect(() => {
    if (idleSsaaEnabled) setRenderDpr(interactDpr);
  }, [idleSsaaEnabled, interactDpr]);
  const canvasDprProp: number | [number, number] = idleSsaaEnabled ? renderDpr : [dprLo, dprHi];

  const canvasBg = (() => {
    switch (bgMode) {
      case 'solid': return bgSolidColor;
      case 'gradient': {
        if (!bgGradient) return '#1a1a1e';
        if (bgGradient.type === 'radial') return `radial-gradient(circle, ${bgGradient.color1}, ${bgGradient.color2})`;
        return `linear-gradient(${bgGradient.angle}deg, ${bgGradient.color1}, ${bgGradient.color2})`;
      }
      case 'image': return '#1a1a1e';
      case 'transparent': {
        const sz = 10;
        return `repeating-conic-gradient(#808080 0% 25%, #c0c0c0 0% 50%) 0 0 / ${sz}px ${sz}px`;
      }
      default: return showHdriBg ? undefined : '#1a1a1e';
    }
  })();

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {needTransparentGL && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: canvasBg,
            filter: bgFilter,
            zIndex: 0,
          }}
        />
      )}
    <Canvas
      key={nativeMsaa ? 'gl-native-msaa' : composerOn || postfx?.antialiasing === 'taa' ? 'gl-composer' : 'gl-no-aa'}
      shadows={shadows
        ? (shadowType === 'basic' ? 'basic'
          : shadowType === 'pcf' || shadowType === 'pcfsoft' ? 'percentage'
          : shadowType === 'vsm' ? 'variance'
          : 'percentage') as any
        : false}
      camera={{ position: [4, 3, 4], fov: 50, near: 0.01, far: 2000 }}
      dpr={canvasDprProp}
      gl={{
        antialias: nativeMsaa,
        alpha: true,
        preserveDrawingBuffer: true,
      }}
      style={{
        position: needTransparentGL ? 'absolute' : undefined,
        inset: needTransparentGL ? 0 : undefined,
        zIndex: needTransparentGL ? 1 : undefined,
        background: needTransparentGL ? 'transparent' : canvasBg,
      }}
      data-screenshot-canvas
      onCreated={({ gl }) => {
        patchCompileAsyncOnRenderer(gl);
      }}
    >
      {idleSsaaEnabled && (
        <IdleSupersampler
          interactDpr={interactDpr}
          desiredIdleDpr={desiredIdleDpr}
          onDpr={setRenderDpr}
        />
      )}
      <TextureAnisotropyCap
        maxAnisotropy={postfx?.textureMaxAnisotropy ?? 16}
        lodBias={typeof postfx?.textureLodBias === 'number' ? postfx.textureLodBias : 0.9}
      />
      <TransmissionQuality scale={1} />
      <SceneBgController
        transparent={needTransparentGL}
        solidColor={useSceneGradientBg || useSceneBgImage ? undefined : solidBgColor}
      />
      {useSceneGradientBg && bgGradient && (
        <SceneGradientBackground gradient={bgGradient} />
      )}
      {useSceneBgImage && (
        <SceneBackgroundImage
          url={bgImageUrl!}
          fit={bgImageFit}
          brightness={bgImageAdjust?.brightness}
          contrast={bgImageAdjust?.contrast}
          saturation={bgImageAdjust?.saturation}
          temperature={bgImageAdjust?.temperature}
        />
      )}
      <Suspense fallback={null}>
        <ConfigureGltfLoaders />
        {hdrUrl ? (
          <ResolvedHdrEnvironment
            hdrUrl={hdrUrl}
            background={showHdriBg}
            blur={envBgBlur}
            environmentIntensity={envIntensity}
            resolution={256}
          />
        ) : (
          <Environment
            preset={environmentPreset as any}
            background={showHdriBg}
            blur={envBgBlur}
            environmentIntensity={envIntensity}
            resolution={256}
          />
        )}
        <EnvironmentRotationSync enabled={envLinkToCamera} rotationY={envRotationY} flipH={envFlipH} flipV={envFlipV} />

        <NestedTransmissionApplier />
        {children}
      </Suspense>

      {/*
        Do not use Canvas onPointerMissed — it can fire on pointerdown when starting an orbit/pan on
        empty space, clearing selection before the drag is recognized. Instead, a pickable transparent
        backdrop receives fiber's onClick after a click on empty space. We additionally track the
        pointer-down position and ignore the "click" when the pointer moved past a small threshold,
        so orbit/pan drags that release over the backdrop no longer clear the selection (which would
        otherwise exit isolate mode). See onPointerDown/onClick below.
      */}
      {onPointerMissed && (
        <mesh
          renderOrder={-9999}
          onPointerDown={(e) => {
            if (e.nativeEvent.button !== 0) return;
            backdropDownRef.current = { x: e.nativeEvent.clientX, y: e.nativeEvent.clientY };
          }}
          onClick={(e) => {
            if (e.nativeEvent.button !== 0) return;
            const down = backdropDownRef.current;
            backdropDownRef.current = null;
            if (down) {
              const dx = e.nativeEvent.clientX - down.x;
              const dy = e.nativeEvent.clientY - down.y;
              // Treat as an orbit/pan drag, not a deselect click.
              if (Math.hypot(dx, dy) > 5) return;
            }
            e.stopPropagation();
            onPointerMissed();
          }}
        >
          <sphereGeometry args={[500, 8, 6]} />
          <meshBasicMaterial
            transparent
            opacity={0}
            depthWrite={false}
            side={THREE.BackSide}
          />
        </mesh>
      )}

      {showGrid && (
        <Grid
          args={[100, 100]}
          cellSize={0.5}
          cellThickness={0.5}
          cellColor="#4a4a4a"
          sectionSize={5}
          sectionThickness={1}
          sectionColor="#6a6a6a"
          fadeDistance={30}
          fadeStrength={1}
          followCamera={false}
          infiniteGrid
        />
      )}

      {showAxes && (
        <GizmoHelper alignment="bottom-right" margin={[60, 60]}>
          <GizmoViewport labelColor="white" axisHeadScale={0.8} />
        </GizmoHelper>
      )}

      {selectedObjectRef && (
        <SelectionOutlineSync selectedRef={selectedObjectRef} outlineRef={outlineRef} />
      )}

      {selectedObjectRef && (
        <TransformGizmo
          selectedRef={selectedObjectRef}
          mode={transformMode}
          space={transformSpace}
          snapEnabled={snapEnabled}
          snapTranslate={snapTranslate}
          snapRotate={snapRotate}
          snapScale={snapScale}
          onTransformChange={onTransformChange}
        />
      )}

      <OrthoViewManager orthoView={orthoView} />
      <OrbitControls
        key={orthoView ? `ortho-${orthoView}` : 'persp'}
        makeDefault
        enablePan={!cameraLocked && !disableNavigation}
        enableZoom={!cameraLocked && !disableNavigation}
        enableRotate={!cameraLocked && !orthoView && !disableNavigation}
        enableDamping={!orthoView}
        dampingFactor={0.08}
        minPolarAngle={0.00873}
        maxPolarAngle={Math.PI - 0.00873}
      />
      {!orthoView && <FovSpeedSync />}

      <ViewportSync
        position={viewportPosition}
        target={viewportTarget}
        fov={viewportFov}
        near={viewportNear}
        far={viewportFar}
        lensShiftX={viewportLensShiftX}
        lensShiftY={viewportLensShiftY}
        safeFrameAspect={safeFrameAspect}
        onApplied={onViewportApplied}
      />

      {postfx?.antialiasing === 'taa' ? (
        <EditorTaaRoot enabled resetKey={postfxTaaResetKey}>
          <PostFXLayer
            settings={postfx}
            selectedRef={selectedObjectRef}
            outlineRef={outlineRef}
            taaResolve
          />
        </EditorTaaRoot>
      ) : composerOn ? (
        <PostFXLayer
          settings={postfx}
          selectedRef={selectedObjectRef}
          outlineRef={outlineRef}
        />
      ) : null}
      {postfx && (
        <ExposureController
          exposure={postfx.exposure}
          toneMapping={postfx.toneMapping}
          applyRendererToneMap={postFxUsesRendererToneMap(postfx)}
        />
      )}
    </Canvas>
    </div>
  );
}
